import { useAuth0 } from "@auth0/auth0-react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { GenderApiValues } from "@utils/constants";
import { setGenderValue } from "@utils/helpers";
import { getPayorDisplayName } from "@utils/payorConfig";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Badge, Button, Headline, Label, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerPayorPayorTypeChoices,
  CreateLearnerCaregiverMutationVariables,
  PayorType,
  useCreateLearnerCaregiverMutation,
  useLearnersQuery,
  useSetLearnerPayorMutation,
  useUpdateLearnerGeneralDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { DoubleColumnContainer, FormContainer, TripleColumnHeader } from "@components/forms/FormLayout";
import SubmitButton from "@components/forms/FormSubmitButton/FormSubmitButton";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import {
  AddUpdateCaregiverDialogue,
  caregiverListType,
} from "@pages/ClientDetailsSubPages/Caregivers/AddUpdateCaregiverDialogue";
import { getGenderValue } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/helpers";
import { USA_STATES_LIST } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/state";
import { useAdminData } from "@providers/AdminDataProvider";

interface NewClientLiteProps {
  setIsFormDirty?: (val: boolean) => void;
}

type CaregiverCard = caregiverListType & { localKey: string };

type AddressFields = {
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type NewClientFormShape = CreateLearnerCaregiverMutationVariables & {
  address?: AddressFields;
  payorType?: string;
};

const toCaregiverPayload = ({ localKey: _localKey, ...caregiver }: CaregiverCard) => caregiver;

const toCreateCaregiverInput = (caregiver: CaregiverCard | null) =>
  caregiver
    ? { ...toCaregiverPayload(caregiver), smsAgreement: Boolean(caregiver.smsAgreement), isPrimary: true }
    : undefined;

const hasAnyAddressField = (address?: AddressFields) => Object.values(address ?? {}).some((value) => !!value);

// Sentinel id for the "None" payor option — lets someone back out of an
// accidental selection instead of being stuck once they've picked one.
// Must be a non-empty string: FormSelect falls back to storing the option's
// display text (e.g. "None") whenever `id` is falsy, so "" would round-trip
// back in as the truthy string "None" instead of clearing the field.
const NO_PAYOR_OPTION_ID = "NONE";

const isRealPayorSelection = (payorType?: string) => !!payorType && payorType !== NO_PAYOR_OPTION_ID;

const getSkippedDetailsErrors = (address?: AddressFields, payorType?: string): string[] =>
  hasAnyAddressField(address) || isRealPayorSelection(payorType)
    ? ["some additional details couldn't be saved. Add them from the client's profile."]
    : [];

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Label colorType="primary" size="medium" displayType="loud">
    {title}
  </Label>
);

export const NewClientLite: React.FC<NewClientLiteProps> = ({ setIsFormDirty }) => {
  const { isPassageHealthUser, roles } = useAdminData();
  const [searchParams] = useSearchParams();

  const { isAuthenticated } = useAuth0();

  const learnersQuery = useLearnersQuery(
    {},
    {
      queryKey: ["learners", isAuthenticated, JSON.stringify(roles)],
      retry: false,
      enabled:
        isAuthenticated &&
        !!roles.length &&
        (roles.includes("admin") || roles.includes("bcba") || roles.includes("assessment-bcba")),
      refetchOnWindowFocus: false,
    },
  );

  const { refetch: refetchLearners } = learnersQuery;

  const formRef = useRef<HTMLFormElement | null>(null);
  const [caregiverList, setCaregiverList] = useState<CaregiverCard[]>([]);
  const [isNewCaregiverDialogueOpen, setIsNewCaregiverDialogueOpen] = useState<boolean>(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState<caregiverListType | null>(null);
  const [caregiverError, setCaregiverError] = useState<string | null>(null);

  const createLearnerCaregiver = useCreateLearnerCaregiverMutation();
  const updateLearnerGeneralDetails = useUpdateLearnerGeneralDetailsMutation();
  const setLearnerPayor = useSetLearnerPayorMutation();

  const go = useNavigate();

  const saveFollowUpDetails = async (
    newLearnerId: string,
    learner: NewClientFormShape["learner"],
    address: AddressFields | undefined,
    payorType: string | undefined,
  ): Promise<string[]> => {
    const followUpErrors: string[] = [];

    // The learner + caregiver are already created by this point — a thrown
    // rejection here must not skip the other follow-up save or bubble up to
    // useFormUtils, which would strand the user on the form (and risk a
    // duplicate client on retry) despite the client already existing.
    if (hasAnyAddressField(address)) {
      try {
        const generalDetailsResult = await updateLearnerGeneralDetails.mutateAsync({
          learner: newLearnerId,
          learnerDetails: {
            firstName: learner.firstName,
            lastName: learner.lastName,
            birthDate: learner.birthDate,
            gender: learner.gender ? setGenderValue(learner.gender) : undefined,
            specifyGender: learner.specifyGender,
            address: address?.address,
            addressLine2: address?.addressLine2,
            city: address?.city,
            state: address?.state,
            zip: address?.zip,
          },
        });
        if (!generalDetailsResult.updateLearnerGeneralDetails?.status) {
          followUpErrors.push(
            `the address couldn't be saved: ${generalDetailsResult.updateLearnerGeneralDetails?.message || "an error occurred"}.`,
          );
        }
      } catch {
        followUpErrors.push("the address couldn't be saved due to a network error.");
      }
    }

    if (isRealPayorSelection(payorType)) {
      try {
        const payorResult = await setLearnerPayor.mutateAsync({
          learnerId: newLearnerId,
          payorTypes: [payorType as unknown as PayorType],
        });
        if (!payorResult.setLearnerPayor?.status) {
          followUpErrors.push(
            `the payor couldn't be saved: ${payorResult.setLearnerPayor?.errors?.[0]?.messages?.[0] ?? "an error occurred"}.`,
          );
        }
      } catch {
        followUpErrors.push("the payor couldn't be saved due to a network error.");
      }
    }

    return followUpErrors;
  };

  const { formState, onSubmit, RegisteredFormInput, RegisteredFormSelected, watch } = useFormUtils<NewClientFormShape>({
    mutationFn: async (params) => {
      if (isPassageHealthUser && caregiverList.length === 0) {
        setCaregiverError("At least one caregiver is required.");
        return;
      }

      const { learner, address, payorType } = params;
      const caregiver = caregiverList[0] ?? null;

      const result = await createLearnerCaregiver.mutateAsync({
        learner: { ...learner, gender: learner.gender ? setGenderValue(learner.gender) : undefined },
        caregiver: toCreateCaregiverInput(caregiver),
        sendIntake: false,
      } as CreateLearnerCaregiverMutationVariables);

      if (!result.createLearnerCaregiver?.status) {
        notifyError(result.createLearnerCaregiver?.message || "Something Went Wrong!");
        return;
      }

      const newLearnerId = result.createLearnerCaregiver.learner?.id;
      const followUpErrors = newLearnerId
        ? await saveFollowUpDetails(newLearnerId, learner, address, payorType)
        : getSkippedDetailsErrors(address, payorType);

      if (followUpErrors.length) {
        notifyError(`Client created, but ${followUpErrors.join(" ")}`);
      } else {
        notifySuccess("Client created successfully");
      }
      refetchLearners();
      go("/clients");
    },
    defaultValues: {
      learner: {
        firstName: "",
        lastName: "",
        birthDate: "",
        gender: undefined,
      },
    },
  });

  // calculation for tracks any change in the form.

  const initialValues = useRef(watch());

  useEffect(() => {
    const currentValues = watch();
    if (
      (JSON.stringify(currentValues) !== JSON.stringify(initialValues.current) || caregiverList.length) &&
      setIsFormDirty
    ) {
      setIsFormDirty(true);
    }
  }, [watch, caregiverList]);

  const openAddCaregiverDialog = () => {
    setSelectedCaregiver(null);
    setIsNewCaregiverDialogueOpen(true);
  };

  const handleEdit = (caregiver: CaregiverCard) => {
    setSelectedCaregiver(caregiver);
    setIsNewCaregiverDialogueOpen(true);
  };

  const handleDelete = () => {
    setCaregiverList([]);
  };

  const genderWatch = watch("learner.gender");

  return (
    <div className="w-full">
      <FormContainer onSubmit={onSubmit} ref={formRef}>
        <div className={clsx(!searchParams.get("client-type") && "mt-5", "flex w-full flex-col items-center")}>
          <div className="flex w-full max-w-[800px] flex-col rounded-2xl border border-limestone-200 bg-white">
            <div className="flex flex-col gap-6 px-8 pt-8 pb-6">
              <Headline size="small">Create a new client</Headline>

              <div className="flex flex-col gap-3">
                <div className="gap-x-4 lg:grid lg:grid-cols-2">
                  <RegisteredFormInput
                    formKey="learner.firstName"
                    inputSize="full"
                    formState={formState}
                    label="First Name"
                  />
                  <RegisteredFormInput
                    formKey="learner.lastName"
                    inputSize="full"
                    formState={formState}
                    label="Last Name"
                  />
                </div>
                <RegisteredFormInput
                  formKey="learner.birthDate"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  label="Date of Birth"
                />
                <DoubleColumnContainer>
                  <RegisteredFormSelected
                    formKey="learner.gender"
                    formState={formState}
                    placeholderText={""}
                    title="Gender"
                    items={
                      Object.values(GenderApiValues).map((o) => ({
                        primary: getGenderValue(o),
                      })) as SelectItem[]
                    }
                  />
                  {genderWatch === "Gender identity is not listed" && (
                    <RegisteredFormInput
                      formKey="learner.specifyGender"
                      formState={formState}
                      required={false}
                      label="Specify Gender"
                    />
                  )}
                </DoubleColumnContainer>
              </div>
            </div>

            <div className="border-t border-limestone-200" />

            <div className="flex flex-col gap-3 px-8 py-6">
              <SectionHeader title="Address Information" />
              <RegisteredFormInput
                formKey="address.address"
                inputSize="full"
                formState={formState}
                required={false}
                label="Address Line 1"
              />
              <RegisteredFormInput
                formKey="address.addressLine2"
                inputSize="full"
                formState={formState}
                required={false}
                label="Address Line 2"
              />
              <TripleColumnHeader>
                <RegisteredFormInput
                  formKey="address.city"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="City"
                />
                <RegisteredFormSelected
                  formKey="address.state"
                  formState={formState}
                  placeholderText=""
                  title="State"
                  required={false}
                  items={USA_STATES_LIST.map((o) => ({ primary: o })) as SelectItem[]}
                />
                <RegisteredFormInput
                  formKey="address.zip"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="Zipcode"
                />
              </TripleColumnHeader>
            </div>

            <div className="border-t border-limestone-200" />

            <div className="flex flex-col gap-3 px-8 py-6">
              <SectionHeader title="Payor Information" />
              <Small colorType="secondary">
                Assigning a payor to this client helps ensure the right report template context is used in their
                reports.
              </Small>
              <RegisteredFormSelected
                formKey="payorType"
                formState={formState}
                placeholderText=""
                title="Payor Name"
                required={false}
                items={
                  [
                    { id: NO_PAYOR_OPTION_ID, primary: "None" },
                    ...Object.values(ApiLearnerPayorPayorTypeChoices).map((id) => ({
                      id,
                      primary: getPayorDisplayName(id),
                    })),
                  ] as SelectItem[]
                }
              />
            </div>

            <div className="border-t border-limestone-200" />

            <div className="flex flex-col gap-3 px-8 pt-6 pb-8">
              <SectionHeader title="Caregiver Details" />
              {caregiverList.length ? (
                <div className="flex flex-col gap-3">
                  {caregiverList.map((caregiver) => (
                    <div
                      key={caregiver.localKey}
                      className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 rounded-lg border bg-white p-3 shadow-xs"
                    >
                      <Small
                        displayType="loud"
                        className="ph-no-capture"
                      >{`${caregiver.firstName} ${caregiver.lastName}`}</Small>
                      <Small colorType="secondary" className="ph-no-capture">
                        {caregiver.email || "-"}
                      </Small>

                      <div className="flex h-6 items-center">
                        <Badge appearance="success" text="Primary" />
                      </div>

                      <div className="flex gap-3">
                        <PencilIcon className="h-5 w-5 cursor-pointer" onClick={() => handleEdit(caregiver)} />
                        <TrashIcon className="h-5 w-5 cursor-pointer" onClick={handleDelete} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <Small colorType="secondary">No caregivers added yet</Small>
                  <Button
                    text="Add Caregiver"
                    variant="primary"
                    buttonStyle="border"
                    onClick={openAddCaregiverDialog}
                  />
                </div>
              )}
              {caregiverError && <Small className="text-red-500">{caregiverError}</Small>}
            </div>
          </div>

          <div className="flex w-full max-w-[800px] justify-end gap-4 pt-6">
            <Button text="Cancel" variant="primary" buttonStyle="border" onClick={() => go("/clients")} />
            <SubmitButton isLoading={formState.isSubmitting} buttonText="Create" />
          </div>
        </div>

        <AddUpdateCaregiverDialogue
          isDialogOpen={isNewCaregiverDialogueOpen}
          setDialogOpen={setIsNewCaregiverDialogueOpen}
          onAddCaregiver={(caregiver) => {
            setCaregiverList([{ ...caregiver, isPrimary: true, localKey: crypto.randomUUID() }]);
            setCaregiverError(null);
          }}
          key={Date.now()}
          selectedCaregiver={selectedCaregiver}
          isPrimaryCaregiver={true}
          assignedCaregivers={caregiverList}
          variant="quickAdd"
        />
      </FormContainer>
    </div>
  );
};
